import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Alert,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import axios from "axios";

const TEMPLATE_LIMIT = 50;

const statusLabels = {
  APPROVED: "Aprovado",
  PENDING: "Pendente",
  REJECTED: "Rejeitado",
  PAUSED: "Pausado",
  DISABLED: "Desativado",
  IN_APPEAL: "Em recurso",
};

const categoryLabels = {
  AUTHENTICATION: "Autenticação",
  MARKETING: "Marketing",
  UTILITY: "Utilidade",
};

const qualityLabels = {
  GREEN: "Alta",
  YELLOW: "Média",
  RED: "Baixa",
  UNKNOWN: "Pendente",
};

const componentLabels = {
  HEADER: "Cabeçalho",
  BODY: "Corpo",
  FOOTER: "Rodapé",
  BUTTONS: "Botões",
};

const getStatusColor = (status) => {
  switch (status) {
    case "APPROVED":
      return "success";
    case "PENDING":
    case "IN_APPEAL":
      return "warning";
    case "REJECTED":
    case "PAUSED":
    case "DISABLED":
      return "error";
    default:
      return "default";
  }
};

const getQualityColor = (quality) => {
  switch (quality) {
    case "GREEN":
      return "success";
    case "YELLOW":
      return "warning";
    case "RED":
      return "error";
    default:
      return "default";
  }
};

const formatValue = (value, labels = {}) => {
  if (value === null || value === undefined || value === "") return "--";
  return labels[value] || value;
};

const getComponentText = (components = [], type) => {
  if (!Array.isArray(components)) return "";
  const component = components.find((item) => item?.type === type);
  return component?.text || "";
};

const getCompactPreview = (components = []) => {
  const body = getComponentText(components, "BODY");
  return body.length > 80 ? `${body.slice(0, 80)}...` : body;
};

const formatComponentDetails = (component) => {
  if (!component) return "";
  if (component.type === "BUTTONS" && Array.isArray(component.buttons)) {
    return component.buttons
      .map((button) => [button.type, button.text, button.url, button.phone_number].filter(Boolean).join(" · "))
      .join("\n");
  }
  return component.text || JSON.stringify(component, null, 2);
};

const createEditState = (template) => ({
  name: template?.name || "",
  language: template?.language || "",
  category: template?.category || "",
  header: getComponentText(template?.components, "HEADER"),
  body: getComponentText(template?.components, "BODY"),
  footer: getComponentText(template?.components, "FOOTER"),
});

const createTemplateInitialState = {
  category: "MARKETING",
  type: "STANDARD",
  name: "",
  language: "pt_BR",
  header: "",
  body: "",
  footer: "",
};

const textFieldConfig = {
  header: { label: "Cabeçalho", maxLength: 60 },
  body: { label: "Corpo", maxLength: 1024 },
  footer: { label: "Rodapé", maxLength: 60 },
};

const variableRegex = /\{\{(\d+)\}\}/g;

const extractVariables = (text = "") => {
  const matches = [...String(text).matchAll(variableRegex)];
  return [...new Set(matches.map((match) => Number(match[1])))].sort((a, b) => a - b);
};

const replaceVariablesWithSamples = (text = "", samples = {}) => (
  String(text).replace(variableRegex, (match, variableNumber) => samples[variableNumber] || match)
);

const hasEdgeVariable = (text = "") => {
  const trimmed = String(text).trim();
  return /^\{\{\d+\}\}/.test(trimmed) || /\{\{\d+\}\}$/.test(trimmed);
};

const normalizeTemplateName = (value = "") => (
  value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
);

const normalizeTemplateNameInput = (value = "") => (
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+/, "")
);

const buildTemplateComponents = (templateState, samples) => {
  const components = [];
  const header = templateState.header.trim();
  const body = templateState.body.trim();
  const footer = templateState.footer.trim();

  if (header) {
    const headerComponent = {
      type: "HEADER",
      format: "TEXT",
      text: header,
    };
    const headerVariables = extractVariables(header);

    if (headerVariables.length > 0) {
      headerComponent.example = {
        header_text: headerVariables.map((number) => samples[number] || ""),
      };
    }

    components.push(headerComponent);
  }

  if (body) {
    const bodyComponent = {
      type: "BODY",
      text: body,
    };
    const bodyVariables = extractVariables(body);

    if (bodyVariables.length > 0) {
      bodyComponent.example = {
        body_text: [bodyVariables.map((number) => samples[number] || "")],
      };
    }

    components.push(bodyComponent);
  }

  if (footer) {
    components.push({
      type: "FOOTER",
      text: footer,
    });
  }

  return components;
};

const getApiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;

  if (typeof data === "string") return data;
  if (Array.isArray(data?.errors)) {
    return data.errors
      .map((item) => item?.message || item?.error || item?.detail || String(item))
      .join(" ");
  }
  if (Array.isArray(data?.details)) {
    return data.details
      .map((item) => item?.message || item?.error || item?.detail || String(item))
      .join(" ");
  }
  return data?.message || data?.error || data?.detail || data?.title || fallback;
};

const normalizeComparableComponent = (component) => ({
  type: component?.type,
  format: component?.format,
  text: component?.text || "",
  buttons: component?.buttons || undefined,
});

const areComponentsEqual = (currentComponents, nextComponents) => (
  JSON.stringify((currentComponents || []).map(normalizeComparableComponent)) ===
  JSON.stringify((nextComponents || []).map(normalizeComparableComponent))
);

const PosVendaMensagens = ({ templatesEndpoint = "/produto/posv/agente/whatsapp/templates" }) => {
  const tabIndex = 0;
  const [templates, setTemplates] = useState([]);
  const [afterCursor, setAfterCursor] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editState, setEditState] = useState(createEditState(null));
  const [isCreating, setIsCreating] = useState(false);
  const [createStep, setCreateStep] = useState(0);
  const [createState, setCreateState] = useState(createTemplateInitialState);
  const [variableSamples, setVariableSamples] = useState({});
  const [createError, setCreateError] = useState("");
  const [createSuccess, setCreateSuccess] = useState("");
  const [isSubmittingTemplate, setIsSubmittingTemplate] = useState(false);
  const [editError, setEditError] = useState("");
  const [editSuccess, setEditSuccess] = useState("");
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [sortBy, setSortBy] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");

  const templateColumns = [
    { key: "name", label: "Nome do modelo" },
    { key: "category", label: "Categoria" },
    { key: "language", label: "Idioma" },
    { key: "status", label: "Status" },
    { key: "qualityScore", label: "Qualidade" },
    { key: "preview", label: "Prévia" },
  ];

  const selectedComponents = useMemo(
    () => (Array.isArray(selectedTemplate?.components) ? selectedTemplate.components : []),
    [selectedTemplate]
  );

  const sortedTemplates = useMemo(() => {
    const getSortValue = (template, key) => {
      if (key === "preview") return getCompactPreview(template.components);
      return template?.[key] || "";
    };

    return [...templates].sort((firstTemplate, secondTemplate) => {
      const firstValue = String(getSortValue(firstTemplate, sortBy)).toLocaleLowerCase("pt-BR");
      const secondValue = String(getSortValue(secondTemplate, sortBy)).toLocaleLowerCase("pt-BR");
      const comparison = firstValue.localeCompare(secondValue, "pt-BR", { numeric: true, sensitivity: "base" });
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [templates, sortBy, sortDirection]);

  const fetchTemplates = useCallback(async ({ after = null, append = false } = {}) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
      setError("");
    }

    try {
      const token = localStorage.getItem("jwtToken");
      if (!token) {
        throw new Error("Token não encontrado no localStorage.");
      }

      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const response = await axios.get(`${baseURL}${templatesEndpoint}`, {
        params: {
          limit: TEMPLATE_LIMIT,
          ...(after ? { after } : {}),
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const nextTemplates = Array.isArray(response.data?.data) ? response.data.data : [];
      setTemplates((currentTemplates) => (
        append ? [...currentTemplates, ...nextTemplates] : nextTemplates
      ));
      setAfterCursor(response.data?.after || null);
    } catch (fetchError) {
      console.error("Erro ao buscar templates WhatsApp:", fetchError);
      setError("Não foi possível carregar os templates.");
      if (!append) {
        setTemplates([]);
        setAfterCursor(null);
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [templatesEndpoint]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setEditState(createEditState(template));
    setIsEditing(false);
    setEditError("");
    setEditSuccess("");
  };

  const handleBackToList = () => {
    setSelectedTemplate(null);
    setIsEditing(false);
    setIsCreating(false);
    setEditError("");
    setEditSuccess("");
  };

  const handleSortTemplates = (columnKey) => {
    if (sortBy === columnKey) {
      setSortDirection((currentDirection) => (currentDirection === "asc" ? "desc" : "asc"));
      return;
    }

    setSortBy(columnKey);
    setSortDirection("asc");
  };

  const handleEditChange = (field) => (event) => {
    const nextValue = field === "name" ? normalizeTemplateNameInput(event.target.value) : event.target.value;

    setEditState((currentState) => ({
      ...currentState,
      [field]: nextValue,
    }));
    setEditError("");
    setEditSuccess("");
  };

  const handleCreateChange = (field) => (event) => {
    const nextValue = field === "name" ? normalizeTemplateNameInput(event.target.value) : event.target.value;

    setCreateState((currentState) => ({
      ...currentState,
      [field]: nextValue,
    }));
    setCreateError("");
    setCreateSuccess("");
  };

  const createVariables = useMemo(() => {
    const fields = ["header", "body", "footer"];
    return fields.flatMap((field) => (
      extractVariables(createState[field]).map((number) => ({ field, number }))
    ));
  }, [createState]);

  const nextVariableNumber = useMemo(() => {
    const allVariables = createVariables.map((variable) => variable.number);
    return allVariables.length > 0 ? Math.max(...allVariables) + 1 : 1;
  }, [createVariables]);

  const invalidVariableFields = useMemo(() => ({
    header: hasEdgeVariable(createState.header),
    body: hasEdgeVariable(createState.body),
    footer: hasEdgeVariable(createState.footer),
  }), [createState]);

  const hasMissingVariableSamples = useMemo(() => (
    createVariables.some(({ number }) => !String(variableSamples[number] || "").trim())
  ), [createVariables, variableSamples]);

  const canSubmitTemplate = (
    normalizeTemplateName(createState.name) &&
    createState.language &&
    createState.category &&
    createState.body.trim() &&
    !invalidVariableFields.header &&
    !invalidVariableFields.body &&
    !invalidVariableFields.footer &&
    !hasMissingVariableSamples &&
    !isSubmittingTemplate
  );

  const editInvalidVariableFields = useMemo(() => ({
    header: hasEdgeVariable(editState.header),
    body: hasEdgeVariable(editState.body),
    footer: hasEdgeVariable(editState.footer),
  }), [editState]);

  const canSaveTemplate = (
    selectedTemplate?.id &&
    normalizeTemplateName(editState.name) &&
    editState.language &&
    editState.category &&
    editState.body.trim() &&
    !editInvalidVariableFields.header &&
    !editInvalidVariableFields.body &&
    !editInvalidVariableFields.footer &&
    !isSavingTemplate
  );

  const handleAddVariable = (field) => {
    const variable = `{{${nextVariableNumber}}}`;
    setCreateState((currentState) => {
      const currentValue = currentState[field] || "";
      const separator = currentValue && !/\s$/.test(currentValue) ? " " : "";
      return {
        ...currentState,
        [field]: `${currentValue}${separator}${variable}`,
      };
    });
    setCreateError("");
    setCreateSuccess("");
  };

  const handleSampleChange = (variableNumber) => (event) => {
    setVariableSamples((currentSamples) => ({
      ...currentSamples,
      [variableNumber]: event.target.value,
    }));
    setCreateError("");
    setCreateSuccess("");
  };

  const handleStartCreate = () => {
    setSelectedTemplate(null);
    setIsEditing(false);
    setIsCreating(true);
    setCreateStep(0);
    setCreateState(createTemplateInitialState);
    setVariableSamples({});
    setCreateError("");
    setCreateSuccess("");
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setCreateStep(0);
    setCreateState(createTemplateInitialState);
    setVariableSamples({});
    setCreateError("");
    setCreateSuccess("");
  };

  const handleSubmitTemplate = async () => {
    if (!canSubmitTemplate) {
      setCreateError("Preencha nome, corpo e amostras das variáveis antes de enviar.");
      return;
    }

    setIsSubmittingTemplate(true);
    setCreateError("");
    setCreateSuccess("");

    try {
      const token = localStorage.getItem("jwtToken");
      if (!token) {
        throw new Error("Token não encontrado no localStorage.");
      }

      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const payload = {
        name: normalizeTemplateName(createState.name),
        language: createState.language,
        category: createState.category,
        allowCategoryChange: true,
        components: buildTemplateComponents(createState, variableSamples),
      };

      await axios.post(`${baseURL}${templatesEndpoint}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      handleCancelCreate();
      setCreateSuccess("Modelo enviado para análise com sucesso.");
      fetchTemplates();
    } catch (submitError) {
      console.error("Erro ao criar template WhatsApp:", submitError);
      setCreateError(getApiErrorMessage(submitError, "Não foi possível enviar o modelo para análise."));
    } finally {
      setIsSubmittingTemplate(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!selectedTemplate?.id) {
      setEditError("ID do template não encontrado.");
      return;
    }

    if (!canSaveTemplate) {
      setEditError("Preencha nome, idioma, categoria e corpo antes de salvar.");
      return;
    }

    setIsSavingTemplate(true);
    setEditError("");
    setEditSuccess("");

    try {
      const token = localStorage.getItem("jwtToken");
      if (!token) {
        throw new Error("Token não encontrado no localStorage.");
      }

      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const nextComponents = buildTemplateComponents(editState, {});
      const payload = {};
      const nextName = normalizeTemplateName(editState.name);

      if (nextName !== selectedTemplate.name) {
        payload.name = nextName;
      }

      if (editState.language !== selectedTemplate.language) {
        payload.language = editState.language;
      }

      if (editState.category !== selectedTemplate.category) {
        payload.category = editState.category;
        payload.allowCategoryChange = true;
      }

      if (!areComponentsEqual(selectedTemplate.components, nextComponents)) {
        payload.components = nextComponents;
      }

      if (Object.keys(payload).length === 0) {
        setEditError("Nenhuma alteração foi identificada.");
        setIsSavingTemplate(false);
        return;
      }

      const response = await axios.put(
        `${baseURL}${templatesEndpoint}/${encodeURIComponent(selectedTemplate.id)}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const updatedTemplate = {
        ...selectedTemplate,
        name: payload.name || selectedTemplate.name,
        language: payload.language || selectedTemplate.language,
        category: payload.category || selectedTemplate.category,
        components: payload.components || selectedTemplate.components,
      };

      setSelectedTemplate(updatedTemplate);
      setTemplates((currentTemplates) => currentTemplates.map((template) => (
        template.id === selectedTemplate.id ? updatedTemplate : template
      )));
      setEditSuccess(
        response.data?.success === false
          ? "A API respondeu, mas não confirmou a alteração do modelo."
          : `Alteração enviada com sucesso (HTTP ${response.status}). A Meta pode levar alguns instantes para refletir o novo conteúdo.`
      );
    } catch (saveError) {
      console.error("Erro ao alterar template WhatsApp:", saveError);
      setEditError(getApiErrorMessage(saveError, "Não foi possível salvar as alterações do modelo."));
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const renderTemplateList = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Paper
          elevation={0}
          sx={{ p: { xs: 2, sm: 3 }, border: "1px solid", borderColor: "divider", borderRadius: 2, textAlign: "center" }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            {error}
          </Typography>
          <Button variant="contained" onClick={() => fetchTemplates()} sx={{ textTransform: "none" }}>
            Tentar novamente
          </Button>
        </Paper>
      );
    }

    if (templates.length === 0) {
      return (
        <Paper
          elevation={0}
          sx={{ p: { xs: 2, sm: 3 }, border: "1px solid", borderColor: "divider", borderRadius: 2, textAlign: "center" }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Nenhum template cadastrado.
          </Typography>
        </Paper>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {createSuccess && (
          <Alert severity="success" onClose={() => setCreateSuccess("")}>
            {createSuccess}
          </Alert>
        )}
        <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {templateColumns.slice(0, 5).map((column) => (
                  <TableCell key={column.key} sortDirection={sortBy === column.key ? sortDirection : false} sx={{ fontWeight: 700 }}>
                    <TableSortLabel
                      active={sortBy === column.key}
                      direction={sortBy === column.key ? sortDirection : "asc"}
                      onClick={() => handleSortTemplates(column.key)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                {templateColumns.slice(5).map((column) => (
                  <TableCell key={column.key} sortDirection={sortBy === column.key ? sortDirection : false} sx={{ fontWeight: 700 }}>
                    <TableSortLabel
                      active={sortBy === column.key}
                      direction={sortBy === column.key ? sortDirection : "asc"}
                      onClick={() => handleSortTemplates(column.key)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedTemplates.map((template) => (
                <TableRow
                  key={template.id || template.name}
                  hover
                  onClick={() => handleSelectTemplate(template)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>
                      {template.name || "Template sem nome"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {template.id ? `ID ${template.id}` : ""}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatValue(template.category, categoryLabels)}</TableCell>
                  <TableCell>{formatValue(template.language)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={formatValue(template.status, statusLabels)}
                      color={getStatusColor(template.status)}
                      sx={{ fontWeight: 700 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={formatValue(template.qualityScore, qualityLabels)}
                      color={getQualityColor(template.qualityScore)}
                      variant={template.qualityScore ? "filled" : "outlined"}
                      sx={{ fontWeight: 700 }}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography variant="body2" sx={{ color: "text.secondary", overflowWrap: "anywhere" }}>
                      {getCompactPreview(template.components) || "--"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {templates.length} modelo{templates.length === 1 ? "" : "s"} carregado{templates.length === 1 ? "" : "s"}.
        </Typography>

        {afterCursor && (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
            <Button
              variant="outlined"
              onClick={() => fetchTemplates({ after: afterCursor, append: true })}
              disabled={isLoadingMore}
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              {isLoadingMore ? "Carregando..." : "Carregar mais"}
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  const renderTemplateDetail = () => (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "340px minmax(0, 1fr)" }, gap: 2 }}>
      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Seu modelo
          </Typography>
        </Box>
        <Box sx={{ p: 2, bgcolor: "#f1eee8" }}>
          <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, maxWidth: 280 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {getComponentText(selectedComponents, "HEADER") || selectedTemplate?.name || "Template"}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-line" }}>
              {getComponentText(selectedComponents, "BODY") || "Corpo não disponível."}
            </Typography>
            {getComponentText(selectedComponents, "FOOTER") && (
              <Typography variant="caption" sx={{ display: "block", mt: 1.5, color: "text.secondary" }}>
                {getComponentText(selectedComponents, "FOOTER")}
              </Typography>
            )}
          </Paper>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2.5 }}>
        {editSuccess && (
          <Alert severity="success" onClose={() => setEditSuccess("")} sx={{ mb: 2 }}>
            {editSuccess}
          </Alert>
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary", overflowWrap: "anywhere" }}>
              {selectedTemplate?.name || "Template sem nome"}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              <Chip size="small" label={formatValue(selectedTemplate?.status, statusLabels)} color={getStatusColor(selectedTemplate?.status)} sx={{ fontWeight: 700 }} />
              <Chip size="small" label={formatValue(selectedTemplate?.category, categoryLabels)} variant="outlined" />
              <Chip size="small" label={formatValue(selectedTemplate?.language)} variant="outlined" />
            </Box>
          </Box>
        </Box>

        {selectedTemplate?.rejectedReason && (
          <Typography variant="body2" sx={{ color: "error.main", mb: 2 }}>
            Motivo da rejeição: {selectedTemplate.rejectedReason}
          </Typography>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {selectedComponents.map((component, index) => (
            <Box key={`${component.type}-${index}`}>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                {componentLabels[component.type] || component.type || "Componente"}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  whiteSpace: "pre-line",
                  overflowWrap: "anywhere",
                  color: "text.primary",
                }}
              >
                {formatComponentDetails(component)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>
    </Box>
  );

  const renderTemplateEdit = () => (
    <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {selectedTemplate?.name || "Editar modelo"}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Edição local para revisão do conteúdo.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => setIsEditing(false)} sx={{ textTransform: "none", borderRadius: 2 }}>
          Voltar ao detalhe
        </Button>
      </Box>

      {isSavingTemplate && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Salvando alterações...
        </Alert>
      )}

      {editSuccess && (
        <Alert severity="success" onClose={() => setEditSuccess("")} sx={{ mb: 2 }}>
          {editSuccess}
        </Alert>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 220px 220px" }, gap: 2, mb: 2 }}>
        <TextField
          label="Nome do modelo"
          value={editState.name}
          onChange={handleEditChange("name")}
          size="small"
          fullWidth
          helperText={`${editState.name.length}/512`}
        />
        <TextField
          select
          label="Idioma"
          value={editState.language}
          onChange={handleEditChange("language")}
          size="small"
          fullWidth
        >
          <MenuItem value="pt_BR">Português (BR)</MenuItem>
          <MenuItem value="en_US">English (US)</MenuItem>
          <MenuItem value="es_ES">Español</MenuItem>
        </TextField>
        <TextField
          select
          label="Categoria"
          value={editState.category}
          onChange={handleEditChange("category")}
          size="small"
          fullWidth
        >
          <MenuItem value="MARKETING">Marketing</MenuItem>
          <MenuItem value="UTILITY">Utilidade</MenuItem>
          <MenuItem value="AUTHENTICATION">Autenticação</MenuItem>
        </TextField>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField
          label="Cabeçalho"
          value={editState.header}
          onChange={handleEditChange("header")}
          size="small"
          fullWidth
          error={editInvalidVariableFields.header}
          helperText={editInvalidVariableFields.header ? "As variáveis não podem estar no início ou no fim do campo." : `${editState.header.length}/60`}
          inputProps={{ maxLength: 60 }}
        />
        <TextField
          label="Corpo"
          value={editState.body}
          onChange={handleEditChange("body")}
          multiline
          minRows={6}
          fullWidth
          error={editInvalidVariableFields.body}
          helperText={editInvalidVariableFields.body ? "As variáveis não podem estar no início ou no fim do campo." : `${editState.body.length}/1024`}
          inputProps={{ maxLength: 1024 }}
        />
        <TextField
          label="Rodapé"
          value={editState.footer}
          onChange={handleEditChange("footer")}
          size="small"
          fullWidth
          error={editInvalidVariableFields.footer}
          helperText={editInvalidVariableFields.footer ? "As variáveis não podem estar no início ou no fim do campo." : `${editState.footer.length}/60`}
          inputProps={{ maxLength: 60 }}
        />
      </Box>

      {editError && (
        <Alert severity="error" onClose={() => setEditError("")} sx={{ mt: 2 }}>
          {editError}
        </Alert>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 2 }}>
        <Button variant="outlined" onClick={() => setEditState(createEditState(selectedTemplate))} sx={{ textTransform: "none", borderRadius: 2 }}>
          Descartar
        </Button>
        <Button
          variant="contained"
          disabled={!canSaveTemplate}
          onClick={handleSaveTemplate}
          sx={{ textTransform: "none", borderRadius: 2, minWidth: 140 }}
        >
          {isSavingTemplate ? "Salvando..." : "Salvar alterações"}
        </Button>
      </Box>
    </Paper>
  );

  const renderTemplatePreview = ({ header, body, footer, name, samples = {} }) => (
    <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
      <Box sx={{ px: 1.75, py: 1.25, borderBottom: "1px solid", borderColor: "divider" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Prévia do modelo
        </Typography>
      </Box>
      <Box sx={{ bgcolor: "#efeae2", minHeight: 300, color: "#111b21" }}>
        <Box
          sx={{
            px: 1.25,
            py: 1,
            bgcolor: "#f0f2f5",
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderBottom: "1px solid #d6dadd",
          }}
        >
          <Box sx={{ width: 30, height: 30, borderRadius: "50%", bgcolor: "#25d366", display: "grid", placeItems: "center", color: "#fff" }}>
            <WhatsAppIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: "#111b21", fontWeight: 700, fontSize: 12, lineHeight: 1.2 }}>
              Pós-Venda
            </Typography>
            <Typography sx={{ color: "#667781", fontSize: 10, lineHeight: 1.2 }}>
              WhatsApp Business
            </Typography>
          </Box>
        </Box>
        <Box sx={{ p: 1.5, display: "flex", justifyContent: "flex-end", alignItems: "flex-start", minHeight: 245 }}>
        <Paper elevation={0} sx={{ p: 1.25, borderRadius: "8px 0 8px 8px", bgcolor: "#d9fdd3", maxWidth: "94%", boxShadow: "0 1px 1px rgba(0, 0, 0, 0.08)" }}>
          {(header || name) && (
            <Typography variant="subtitle2" sx={{ color: "#111b21", fontWeight: 700, overflowWrap: "anywhere" }}>
              {replaceVariablesWithSamples(header || name, samples)}
            </Typography>
          )}
          <Typography variant="body2" sx={{ color: "#111b21", mt: header || name ? 1 : 0, lineHeight: 1.4, whiteSpace: "pre-line", overflowWrap: "anywhere" }}>
            {replaceVariablesWithSamples(body || "Digite o corpo da mensagem para visualizar a prévia.", samples)}
          </Typography>
          {footer && (
            <Typography variant="caption" sx={{ display: "block", mt: 1.5, color: "#667781", whiteSpace: "pre-line" }}>
              {replaceVariablesWithSamples(footer, samples)}
            </Typography>
          )}
          <Typography sx={{ display: "block", mt: 0.5, color: "#667781", textAlign: "right", fontSize: 9 }}>
            10:42
          </Typography>
        </Paper>
        </Box>
      </Box>
    </Paper>
  );

  const renderCreateStepIndicator = () => (
    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, mb: 1.5 }}>
      {["Configurar modelo", "Editar modelo", "Enviar para análise"].map((label, index) => (
        <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Typography
            variant="body2"
            sx={{
              color: index === createStep ? "primary.main" : index < createStep ? "success.main" : "text.secondary",
              fontWeight: index <= createStep ? 700 : 500,
            }}
          >
            {label}
          </Typography>
          {index < 2 && (
            <Typography variant="body2" sx={{ color: "text.disabled", fontWeight: 700 }}>
              {">>"}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );

  const renderCreateConfigure = () => (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 320px" }, gap: 1.5 }}>
      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: { xs: 1.5, sm: 2 } }}>
        {renderCreateStepIndicator()}
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          Configurar seu modelo
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
          Escolha a categoria que melhor descreve seu modelo de mensagem.
        </Typography>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 1, mb: 1.5 }}>
          {[
            { value: "MARKETING", label: "Marketing" },
            { value: "UTILITY", label: "Utilidade" },
          ].map((option) => (
            <Button
              key={option.value}
              variant={createState.category === option.value ? "contained" : "outlined"}
              onClick={() => setCreateState((currentState) => ({ ...currentState, category: option.value }))}
              sx={{ justifyContent: "center", textTransform: "none", borderRadius: 1.5, py: 0.85 }}
            >
              {option.label}
            </Button>
          ))}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {[
            { value: "STANDARD", label: "Padrão", description: "Envie mensagens com mídia, cabeçalho, rodapé e botões personalizados." },
          ].map((option) => (
            <Paper
              key={option.value}
              elevation={0}
              onClick={() => setCreateState((currentState) => ({ ...currentState, type: option.value }))}
              sx={{
                p: 1.25,
                border: "1px solid",
                borderColor: createState.type === option.value ? "primary.main" : "divider",
                bgcolor: createState.type === option.value ? "primary.50" : "background.paper",
                borderRadius: 1.5,
                cursor: "pointer",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {option.label}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {option.description}
              </Typography>
            </Paper>
          ))}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
          <Button variant="outlined" onClick={handleCancelCreate} sx={{ textTransform: "none", borderRadius: 2 }}>
            Descartar
          </Button>
          <Button variant="contained" onClick={() => setCreateStep(1)} sx={{ textTransform: "none", borderRadius: 2 }}>
            Avançar
          </Button>
        </Box>
      </Paper>

      {renderTemplatePreview({
        name: createState.name || "your_template_name",
        body: createState.body || "Hello",
        samples: variableSamples,
      })}
    </Box>
  );

  const renderCreateTextInput = (field) => {
    const config = textFieldConfig[field];
    const currentValue = createState[field];
    const isBody = field === "body";
    const hasError = invalidVariableFields[field];

    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {config.label}{field !== "body" ? " · Opcional" : ""}
          </Typography>
          <Button
            size="small"
            variant="text"
            startIcon={<AddIcon />}
            onClick={() => handleAddVariable(field)}
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700, py: 0.25 }}
          >
            Adicionar variável
          </Button>
        </Box>
        <TextField
          value={currentValue}
          onChange={handleCreateChange(field)}
          placeholder={
            field === "body"
              ? "Digite o texto principal da mensagem"
              : `Adicione uma pequena linha de texto ao ${config.label.toLowerCase()}`
          }
          size="small"
          multiline={isBody}
          minRows={isBody ? 5 : undefined}
          fullWidth
          error={hasError}
          helperText={hasError ? "As variáveis não podem estar no início ou no fim do campo." : `${currentValue.length}/${config.maxLength}`}
          inputProps={{ maxLength: config.maxLength }}
        />
      </Box>
    );
  };

  const renderVariableSamples = () => {
    if (createVariables.length === 0) return null;

    return (
      <Paper elevation={0} sx={{ bgcolor: "action.hover", borderRadius: 2, p: { xs: 1.5, sm: 2 } }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
          Amostras de variáveis
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
          Adicione um exemplo para cada variável. Esses valores são usados apenas na análise do modelo e na prévia.
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {createVariables.map(({ field, number }) => (
            <Box
              key={`${field}-${number}`}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "160px minmax(0, 1fr)" },
                gap: 1,
                alignItems: "flex-start",
              }}
            >
              <TextField
                value={`{{${number}}}`}
                size="small"
                disabled
                helperText={textFieldConfig[field].label}
              />
              <TextField
                value={variableSamples[number] || ""}
                onChange={handleSampleChange(number)}
                size="small"
                fullWidth
                error={!variableSamples[number]}
                placeholder={`Insira conteúdo para {{${number}}}`}
                helperText={!variableSamples[number] ? "Adicionar texto de amostra" : " "}
              />
            </Box>
          ))}
        </Box>
      </Paper>
    );
  };

  const renderCreateEdit = () => (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 320px" }, gap: 1.5 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: { xs: 1.5, sm: 2 } }}>
          {renderCreateStepIndicator()}
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
            Nome e idioma do modelo
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 220px" }, gap: 1.5 }}>
            <TextField
              label="Nome do modelo"
              value={createState.name}
              onChange={handleCreateChange("name")}
              size="small"
              fullWidth
              helperText={`${createState.name.length}/512`}
            />
            <TextField
              select
              label="Idioma"
              value={createState.language}
              onChange={handleCreateChange("language")}
              size="small"
              fullWidth
            >
              <MenuItem value="pt_BR">Português (BR)</MenuItem>
              <MenuItem value="en_US">English (US)</MenuItem>
              <MenuItem value="es_ES">Español</MenuItem>
            </TextField>
          </Box>
        </Paper>

        <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: { xs: 1.5, sm: 2 } }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            Conteúdo
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
            Adicione cabeçalho, corpo de texto e rodapé para seu modelo.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.35 }}>
            {renderCreateTextInput("header")}
            {renderCreateTextInput("body")}
            {renderVariableSamples()}
            {renderCreateTextInput("footer")}
          </Box>
        </Paper>

        {createError && (
          <Alert severity="error" onClose={() => setCreateError("")}>
            {createError}
          </Alert>
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.5 }}>
          <Button variant="outlined" onClick={() => setCreateStep(0)} sx={{ textTransform: "none", borderRadius: 2 }}>
            Voltar
          </Button>
          <Button
            variant="contained"
            disabled={!canSubmitTemplate}
            onClick={handleSubmitTemplate}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            {isSubmittingTemplate ? "Enviando..." : "Enviar para análise"}
          </Button>
        </Box>
      </Box>

      {renderTemplatePreview({ ...createState, samples: variableSamples })}
    </Box>
  );

  const renderTemplateCreate = () => (
    createStep === 0 ? renderCreateConfigure() : renderCreateEdit()
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Paper
        elevation={1}
        sx={{
          borderRadius: 2,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <Tabs
          value={tabIndex}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: { xs: "0.8rem", sm: "0.9rem" },
              fontWeight: 500,
              minWidth: { xs: 100, sm: 160 },
              py: 1.5,
              "& .MuiTab-iconWrapper": {
                marginBottom: "4px !important",
              },
            },
          }}
        >
          <Tab icon={<ArticleOutlinedIcon sx={{ fontSize: "18px" }} />} label="Templates" />
        </Tabs>

        <Box
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 1,
            backgroundColor: "background.default",
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {(selectedTemplate || isCreating) && (
              <IconButton size="small" onClick={handleBackToList} aria-label="Voltar para templates">
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            )}
            <Typography
              variant="body2"
              sx={{
                color: "text.primary",
                fontSize: "0.85rem",
                fontStyle: "italic",
                fontWeight: "bold",
              }}
            >
              {isCreating ? "Criar modelo" : selectedTemplate ? selectedTemplate.name || "Detalhe do template" : "Modelos de mensagem"}
            </Typography>
          </Box>

          {!selectedTemplate && !isCreating && (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => fetchTemplates()}
                disabled={isLoading || isLoadingMore}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Atualizar
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleStartCreate}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Criar modelo
              </Button>
            </Box>
          )}
        </Box>

        <Box sx={{ p: { xs: 1.5, sm: isCreating ? 2 : 3 } }}>
          {!selectedTemplate && !isCreating && renderTemplateList()}
          {selectedTemplate && !isEditing && renderTemplateDetail()}
          {selectedTemplate && isEditing && renderTemplateEdit()}
          {isCreating && renderTemplateCreate()}
        </Box>
      </Paper>
    </Box>
  );
};

export default PosVendaMensagens;
